// ============================================================================
// Dérivées et primitives sur les termes du noyau (refonte, lot 5.9, moteur v31).
//
//   - `derive(t, x)` : la dérivée EXACTE d'un terme (règles de dérivation, aucune
//     approximation), mise en forme canonique légère.
//   - `usualDerivative(t, x)` : la dérivée « usuelle » — celle qu'un élève écrit d'un coup,
//     terme à terme : puissances, inverse, racine, exp, ln, cos, sin d'une expression
//     AFFINE (f(ax + b) → a f′(ax + b)), et leurs combinaisons linéaires. null sinon.
//   - `stepTemplate(f, x, known)` : ce qu'on écrit en appliquant UNE règle à f — par
//     terme : usuelle, produit (uv)′ = u′v + uv′, quotient (u′v − uv′) ÷ v², inverse
//     −v′ ÷ v², composée u′ × g′(u). Les dérivées intérieures doivent être usuelles ou
//     déjà écrites (`known`) ; sinon le gabarit est « imbriqué » (deux règles d'un coup).
//   - Les fonctions se comparent par leurs VALEURS en des points de l'ensemble de
//     définition (`Sampler`) : deux expressions égales en 6 points ou plus, à 10⁻⁹ près
//     relatif, sont tenues pour égales (preuve `sampled`, comme l'auto-test des règles).
// ============================================================================

import * as Q from './exact';
import { evalFloat, withRadianArcs } from './normal';
import {
  add,
  canon,
  div,
  fn,
  mul,
  num,
  pow,
  symbolsOf,
  termKey,
  type FnName,
  type Term,
} from './term';

export class CalculusUnsupported extends Error {
  constructor(public readonly what: string) {
    super(`dérivation non prise en charge : ${what}`);
  }
}

export const dependsOn = (t: Term, x: string): boolean => symbolsOf(t).has(x);

/*
 * Moteur v60 (lot 6.65) : arctan, arcsin, arccos se dérivent — EN RADIANS — seulement à
 * l'intérieur de `withArcDerivatives` (intégrales, sommes de Riemann, dérivées et primitives
 * qui les écrivent), où ils s'évaluent aussi en radians (`withRadianArcs`). Ailleurs (feuilles
 * sans arctan, limites v54-v58) rien ne change : ces fonctions restent « non prises en charge ».
 */
let ARCS = false;
const ARC_NAMES = new Set<string>(['arctan', 'arcsin', 'arccos']);
/** exécute `f` avec arctan, arcsin, arccos dérivables et évalués en radians */
export function withArcDerivatives<T>(f: () => T): T {
  const prev = ARCS;
  ARCS = true;
  try {
    return withRadianArcs(f);
  } finally {
    ARCS = prev;
  }
}
/** dans `withArcDerivatives` ? */
export const arcsActive = (): boolean => ARCS;
/** une écriture qui contient arctan, arcsin ou arccos (moteur v60) */
export const ARC_TEXT_RE = /\barc(?:tan|sin|cos)\s*\(/;
const arcsOn = (f: string): boolean => ARCS && ARC_NAMES.has(f);

const ZERO = num(0);
const ONE = num(1);
const isZeroTerm = (t: Term): boolean => t.k === 'num' && Q.isZero(t.v);

/* ------------------------------ dérivée exacte ------------------------------ */

/** Dérivée exacte de `t` par rapport à `x` (forme canonique légère). */
export function derive(t: Term, x: string): Term {
  return canon(d(t, x));
}

function d(t: Term, x: string): Term {
  if (!dependsOn(t, x)) return ZERO;
  switch (t.k) {
    case 'num':
      return ZERO;
    case 'sym':
      return t.n === x ? ONE : ZERO;
    case 'add':
      return add(...t.c.map((c) => d(c, x)));
    case 'mul': {
      const terms: Term[] = [];
      t.c.forEach((c, i) => {
        if (!dependsOn(c, x)) return;
        terms.push(mul(...t.c.map((o, j) => (j === i ? d(c, x) : o))));
      });
      return add(...terms);
    }
    case 'div': {
      const { a, b } = t;
      if (!dependsOn(b, x)) return div(d(a, x), b);
      if (!dependsOn(a, x)) return div(mul(num(-1), a, d(b, x)), pow(b, num(2)));
      return div(add(mul(d(a, x), b), mul(num(-1), a, d(b, x))), pow(b, num(2)));
    }
    case 'pow': {
      const { a, b } = t;
      if (!dependsOn(b, x)) return mul(b, pow(a, add(b, num(-1))), d(a, x));
      if (!dependsOn(a, x)) return mul(fn('ln', a), t, d(b, x));
      return mul(t, add(mul(d(b, x), fn('ln', a)), div(mul(b, d(a, x)), a)));
    }
    case 'fn':
      return mul(d(t.a, x), outer(t.f, t.a));
    case 'binom':
      throw new CalculusUnsupported('k parmi n');
  }
}

/** g′(u) pour une fonction usuelle g (sans le facteur u′). */
function outer(f: FnName, u: Term): Term {
  switch (f) {
    case 'sqrt':
      return div(ONE, mul(num(2), fn('sqrt', u)));
    case 'exp':
      return fn('exp', u);
    case 'ln':
      return div(ONE, u);
    case 'cos':
      return mul(num(-1), fn('sin', u));
    case 'sin':
      return fn('cos', u);
    case 'tan':
      return div(ONE, pow(fn('cos', u), num(2)));
    case 'abs':
      // |u|′ = u′ × u ÷ |u| (u ≠ 0)
      return div(u, fn('abs', u));
    // moteur v60 : en radians, seulement dans `withArcDerivatives`
    case 'arctan':
      if (!ARCS) throw new CalculusUnsupported(f);
      return div(ONE, add(ONE, pow(u, num(2))));
    case 'arcsin':
      if (!ARCS) throw new CalculusUnsupported(f);
      return div(ONE, fn('sqrt', add(ONE, mul(num(-1), pow(u, num(2))))));
    case 'arccos':
      if (!ARCS) throw new CalculusUnsupported(f);
      return div(num(-1), fn('sqrt', add(ONE, mul(num(-1), pow(u, num(2))))));
    default:
      throw new CalculusUnsupported(f);
  }
}

/* ------------------------------ formes ------------------------------ */

/** t est-elle affine en x (ax + b, a ≠ 0) ? */
export function isAffine(t: Term, x: string): boolean {
  if (!dependsOn(t, x)) return false;
  try {
    return !dependsOn(derive(t, x), x);
  } catch {
    return false;
  }
}

/** Les termes d'une somme (forme canonique), un seul terme sinon. */
export function termsOf(t: Term): Term[] {
  const c = canon(t);
  if (isZeroTerm(c)) return [];
  return c.k === 'add' ? c.c : [c];
}

/** Coefficient constant et facteurs qui dépendent de x d'un terme. */
export function splitConstant(t: Term, x: string): { c: Term[]; core: Term[] } {
  const k = canon(t);
  const fs = k.k === 'mul' ? k.c : [k];
  const c: Term[] = [];
  const core: Term[] = [];
  for (const f of fs) {
    if (!dependsOn(f, x)) c.push(f);
    else if (f.k === 'div' && !dependsOn(f.b, x)) {
      // a ÷ 2 : le facteur 1 ÷ 2 est constant
      c.push(div(ONE, f.b));
      core.push(f.a);
    } else core.push(f);
  }
  return { c, core };
}

/* ------------------------------ dérivées usuelles ------------------------------ */

/**
 * Dérivée usuelle (null si t n'est pas usuel) : combinaison linéaire de puissances,
 * d'inverses, de racines, d'exp, ln, cos, sin, tan d'une expression AFFINE, et de aˣ.
 */
export function usualDerivative(t: Term, x: string): Term | null {
  try {
    const r = usual(canon(t), x);
    return r === null ? null : canon(r);
  } catch {
    return null;
  }
}

function usual(t: Term, x: string): Term | null {
  if (!dependsOn(t, x)) return ZERO;
  switch (t.k) {
    case 'sym':
      return t.n === x ? ONE : ZERO;
    case 'add': {
      const ds = t.c.map((c) => usual(c, x));
      return ds.every((v) => v !== null) ? add(...(ds as Term[])) : null;
    }
    case 'mul': {
      const { c, core } = splitConstant(t, x);
      if (core.length !== 1) return null;
      const dc = usual(core[0]!, x);
      return dc === null ? null : mul(...c, dc);
    }
    case 'div': {
      if (!dependsOn(t.b, x)) {
        const da = usual(t.a, x);
        return da === null ? null : div(da, t.b);
      }
      if (dependsOn(t.a, x)) return null;
      // k ÷ (ax + b), k ÷ xⁿ, k ÷ (ax + b)ⁿ, k ÷ (2x²) : une puissance d'exposant négatif
      const { c: dc, core: dcore } = splitConstant(t.b, x);
      if (dcore.length !== 1) return null;
      const base = powerBase(dcore[0]!, x);
      if (!base) return null;
      return mul(div(t.a, dc.length ? mul(...dc) : ONE), d(pow(base.b, num(Q.neg(base.n))), x));
    }
    case 'pow': {
      if (!dependsOn(t.b, x))
        return isAffine(t.a, x) || (t.a.k === 'sym' && t.a.n === x) ? d(t, x) : null;
      if (!dependsOn(t.a, x)) return isAffine(t.b, x) ? d(t, x) : null;
      return null;
    }
    case 'fn':
      if (!['sqrt', 'exp', 'ln', 'cos', 'sin', 'tan'].includes(t.f) && !arcsOn(t.f)) return null;
      return isAffine(t.a, x) ? d(t, x) : null;
    default:
      return null;
  }
}

/** b = (affine)ⁿ, n rationnel constant (n = 1 : b affine). */
function powerBase(b: Term, x: string): { b: Term; n: Q.Rat } | null {
  if (isAffine(b, x)) return { b, n: Q.ONE };
  if (b.k === 'pow' && b.b.k === 'num' && isAffine(b.a, x)) return { b: b.a, n: b.b.v };
  if (b.k === 'fn' && b.f === 'sqrt' && isAffine(b.a, x)) return { b: b.a, n: Q.rat(1, 2) };
  return null;
}

/* ------------------------------ gabarits d'un pas ------------------------------ */

export type StepRule =
  | 'usual'
  | 'product'
  | 'quotient'
  | 'inverse'
  | 'chain_exp'
  | 'chain_ln'
  | 'chain_sqrt'
  | 'chain_pow'
  | 'chain_trig'
  /** moteur v60 : u′ ÷ (1 + u²), u′ ÷ √(1 − u²) */
  | 'chain_arc';

export interface StepTemplate {
  /** les morceaux écrits (un par terme de la formule), avant tout calcul */
  pieces: Term[];
  /** quotient seul : le dénominateur v², le numérateur est `pieces` */
  den?: Term;
  /** règles appliquées (usual seule, ou la règle principale d'abord) */
  rules: StepRule[];
  /** une dérivée intérieure n'était ni usuelle ni écrite : deux règles d'un coup */
  nested: Term[];
}

/** Dérivée d'un morceau : usuelle, ou déjà écrite par l'élève. */
export type KnownDerivative = (part: Term) => Term | null;

function inner(part: Term, x: string, known: KnownDerivative, nested: Term[]): Term {
  const u = usualDerivative(part, x);
  if (u) return u;
  const k = known(part);
  if (k) return k;
  nested.push(part);
  return derive(part, x);
}

/**
 * Ce qu'on écrit en dérivant `f` en appliquant une règle par terme. null : une forme que
 * le lot ne sait pas dériver (k parmi n, |x|…).
 */
export function stepTemplate(f: Term, x: string, known: KnownDerivative): StepTemplate | null {
  try {
    const terms = termsOf(f);
    const out: StepTemplate = { pieces: [], rules: [], nested: [] };
    if (terms.length === 1) {
      const q = quotientTemplate(terms[0]!, x, known);
      if (q) return q;
    }
    for (const t of terms) {
      const r = termTemplate(t, x, known, out.nested);
      if (!r) return null;
      out.pieces.push(...r.pieces.filter((p) => !isZeroTerm(canon(p))));
      if (!out.rules.includes(r.rule)) out.rules.push(r.rule);
    }
    if (out.rules.length > 1) out.rules = out.rules.filter((r) => r !== 'usual');
    return out;
  } catch {
    return null;
  }
}

function termTemplate(
  t: Term,
  x: string,
  known: KnownDerivative,
  nested: Term[],
): { pieces: Term[]; rule: StepRule } | null {
  const u = usualDerivative(t, x);
  if (u) return { pieces: [u], rule: 'usual' };
  const { c, core } = splitConstant(t, x);
  if (core.length >= 2) {
    const ds = core.map((f) => inner(f, x, known, nested));
    return {
      pieces: core.map((_, i) => mul(...c, ...core.map((g, j) => (i === j ? ds[i]! : g)))),
      rule: 'product',
    };
  }
  const g = core[0];
  if (!g) return { pieces: [], rule: 'usual' };
  if (g.k === 'div') {
    if (!dependsOn(g.a, x)) {
      const dv = inner(g.b, x, known, nested);
      return {
        pieces: [div(mul(num(-1), ...c, g.a, dv), pow(g.b, num(2)))],
        rule: 'inverse',
      };
    }
    const du = inner(g.a, x, known, nested);
    const dv = inner(g.b, x, known, nested);
    return {
      pieces: [div(mul(...c, add(mul(du, g.b), mul(num(-1), g.a, dv))), pow(g.b, num(2)))],
      rule: 'quotient',
    };
  }
  if (g.k === 'pow' && !dependsOn(g.b, x)) {
    const du = inner(g.a, x, known, nested);
    return {
      pieces: [mul(...c, g.b, du, pow(g.a, add(g.b, num(-1))))],
      rule: 'chain_pow',
    };
  }
  if (g.k === 'pow' && !dependsOn(g.a, x)) {
    const du = inner(g.b, x, known, nested);
    return { pieces: [mul(...c, fn('ln', g.a), du, g)], rule: 'chain_exp' };
  }
  if (g.k === 'fn' && ['sqrt', 'exp', 'ln', 'cos', 'sin', 'tan'].includes(g.f)) {
    const du = inner(g.a, x, known, nested);
    const rule: StepRule =
      g.f === 'exp'
        ? 'chain_exp'
        : g.f === 'ln'
          ? 'chain_ln'
          : g.f === 'sqrt'
            ? 'chain_sqrt'
            : 'chain_trig';
    return { pieces: [mul(...c, du, outer(g.f, g.a))], rule };
  }
  // moteur v60 : arctan(u), arcsin(u) (seulement dans `withArcDerivatives`)
  if (g.k === 'fn' && arcsOn(g.f)) {
    const du = inner(g.a, x, known, nested);
    return { pieces: [mul(...c, du, outer(g.f, g.a))], rule: 'chain_arc' };
  }
  return null;
}

/** f = c × u ÷ v (u et v dépendent de x) : numérateur u′v − uv′ en deux morceaux, sur v². */
function quotientTemplate(t: Term, x: string, known: KnownDerivative): StepTemplate | null {
  const { c, core } = splitConstant(t, x);
  if (core.length !== 1) return null;
  const g = core[0]!;
  if (g.k !== 'div' || !dependsOn(g.a, x) || !dependsOn(g.b, x)) return null;
  if (usualDerivative(t, x)) return null;
  const nested: Term[] = [];
  const du = inner(g.a, x, known, nested);
  const dv = inner(g.b, x, known, nested);
  return {
    pieces: [mul(...c, du, g.b), mul(num(-1), ...c, g.a, dv)],
    den: pow(g.b, num(2)),
    rules: ['quotient'],
    nested,
  };
}

/* ------------------------------ parties d'une fonction ------------------------------ */

/**
 * Les « parties » de f qu'on peut nommer u, v : facteurs (et produits de facteurs) d'un
 * terme, numérateur et dénominateur, base d'une puissance, expression sous une fonction,
 * termes d'une somme — récursivement.
 */
export function partsOf(f: Term, x: string): Term[] {
  const out: Term[] = [];
  const seen = new Set<string>();
  const push = (t: Term) => {
    const k = canon(t);
    if (!dependsOn(k, x)) return;
    const key = termKey(k);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(k);
    walk(k);
  };
  const walk = (t: Term) => {
    switch (t.k) {
      case 'add':
        t.c.forEach(push);
        break;
      case 'mul': {
        const fs = t.c.filter((f) => dependsOn(f, x));
        fs.forEach(push);
        if (fs.length > 2 && fs.length <= 4)
          for (let m = 1; m < (1 << fs.length) - 1; m++) {
            const sub = fs.filter((_, i) => m & (1 << i));
            if (sub.length >= 2) push(mul(...sub));
          }
        // le coefficient avec un facteur : 3x dans 3x(x² + 2)
        const ks = t.c.filter((f) => !dependsOn(f, x));
        if (ks.length) fs.forEach((f) => push(mul(...ks, f)));
        break;
      }
      case 'div':
        push(t.a);
        push(t.b);
        break;
      case 'pow':
        push(t.a);
        push(t.b);
        break;
      case 'fn':
        push(t.a);
        break;
      default:
        break;
    }
  };
  walk(canon(f));
  return out;
}

/* ------------------------------ comparaison par valeurs ------------------------------ */

/** Points d'essai : irrationnels « ordinaires », loin des entiers et des demis. */
export const SAMPLE_POINTS: readonly number[] = [
  -3.71, -2.29, -1.63, -0.87, -0.61, -0.33, -0.13, 0.17, 0.39, 0.73, 0.91, 1.21, 1.47, 1.93, 2.57,
  3.37, 4.39, 5.83, 7.19,
];

export const MIN_POINTS = 5;

export function closeTo(a: number, b: number): boolean {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
}

/** Les valeurs d'une expression aux points d'essai (NaN : indéfinie). */
export type Vec = number[];

export function valuesAt(
  t: Term,
  x: string,
  points: readonly number[],
  env: Record<string, number> = {},
): Vec {
  return points.map((p) => {
    try {
      const v = evalFloat(t, { ...env, [x]: p });
      return Number.isFinite(v) ? v : NaN;
    } catch {
      return NaN;
    }
  });
}

export function vecEqual(a: Vec, b: Vec): boolean {
  if (a.length !== b.length || a.length < MIN_POINTS) return false;
  return a.every((v, i) => closeTo(v, b[i]!));
}

export function vecAdd(a: Vec, b: Vec): Vec {
  return a.map((v, i) => v + b[i]!);
}

export function vecIsZero(a: Vec): boolean {
  return a.every((v) => closeTo(v, 0));
}

/** a = k × b pour une constante k (null sinon, ou si b s'annule partout). */
export function vecRatio(a: Vec, b: Vec): number | null {
  let k: number | null = null;
  for (let i = 0; i < a.length; i++) {
    if (!Number.isFinite(a[i]!) || !Number.isFinite(b[i]!)) return null;
    if (Math.abs(b[i]!) < 1e-12) {
      if (Math.abs(a[i]!) > 1e-9) return null;
      continue;
    }
    const r = a[i]! / b[i]!;
    if (k === null) k = r;
    else if (!closeTo(r, k)) return null;
  }
  return k;
}

/** a − b est constant (null sinon). */
export function vecOffset(a: Vec, b: Vec): number | null {
  let k: number | null = null;
  for (let i = 0; i < a.length; i++) {
    if (!Number.isFinite(a[i]!) || !Number.isFinite(b[i]!)) return null;
    const r = a[i]! - b[i]!;
    if (k === null) k = r;
    else if (!closeTo(r + 1, k + 1)) return null;
  }
  return k;
}

/**
 * Les termes `got` (valeurs) se regroupent-ils pour donner chacun des morceaux `want` ?
 * Chaque terme écrit va dans un seul groupe ; un groupe vide vaut 0 (un morceau nul peut
 * manquer). C'est « une règle appliquée, les calculs faits DANS chaque morceau ».
 */
export function groupsMatch(got: Vec[], want: Vec[]): boolean {
  const n = got.length;
  const k = want.length;
  if (n === 0) return want.every(vecIsZero);
  if (k === 0) return got.every(vecIsZero);
  if (n > 10 || k > 5) return false;
  const width = got[0]!.length;
  const sums: Vec[] = want.map(() => new Array<number>(width).fill(0));
  const assign = (i: number): boolean => {
    if (i === n) return sums.every((s, j) => s.every((v, p) => closeTo(v, want[j]![p]!)));
    for (let j = 0; j < k; j++) {
      for (let p = 0; p < width; p++) sums[j]![p]! += got[i]![p]!;
      if (assign(i + 1)) return true;
      for (let p = 0; p < width; p++) sums[j]![p]! -= got[i]![p]!;
    }
    return false;
  };
  return assign(0);
}

/** Nombre de nœuds d'un terme tel qu'écrit (taille de l'écriture). */
export function termSize(t: Term): number {
  const k = t;
  switch (k.k) {
    case 'num':
    case 'sym':
      return 1;
    case 'add':
    case 'mul':
      return 1 + k.c.reduce((s, c) => s + termSize(c), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return 1 + termSize(k.a) + termSize(k.b);
    case 'fn':
      return 1 + termSize(k.a);
  }
}
